/**
 * BP-RC1 — domainRouteConfig-derived sidebar menu sections.
 */
import React from 'react';

import {
  appealSubRoutes,
  automationSubRoutes,
  calendarSubRoutes,
  consciousSubRoutes,
  entitySubRoutes,
  eventSubRoutes,
  exportSubRoutes,
  financeAdvancedSubRoutes,
  financeSubRoutes,
  flowSubRoutes,
  inventorySubRoutes,
  itemSubRoutes,
  knowledgeSubRoutes,
  logSubRoutes,
  manufacturingSubRoutes,
  spaceSubRoutes,
  systemSubRoutes,
  workspaceSubRoutes,
  physicalAccessSubRoutes,
  warehouseSubRoutes,
} from './domainRouteConfig';
import {
  buildAbsolutePath,
  resolveRouteId,
  resolveRouteLabel,
  type DomainRouteConfigEntry,
} from './domainRouteConfigSchema';
import type { AppMenuItem } from './appMenuConfig';
import {
  buildDomainNavigationItems,
  buildLeafNavigationItems,
  toAppMenuItems,
} from '../router/generateNavigation';

function stripChildIcons(children: AppMenuItem[] | undefined): AppMenuItem[] | undefined {
  return children?.map((child) => ({ ...child, icon: null }));
}

function withDomainIcon(item: AppMenuItem, icon: React.ReactNode): AppMenuItem {
  const children = stripChildIcons(item.children);
  return {
    ...item,
    icon,
    ...(children !== undefined ? { children } : {}),
  };
}

function leafItemsToAppMenu(
  domainTop: string,
  routes: readonly DomainRouteConfigEntry[]
): AppMenuItem[] {
  return buildLeafNavigationItems(domainTop, routes).map((leaf) => ({
    key: leaf.key,
    path: leaf.path,
    icon: null,
    label: leaf.label,
  }));
}

interface StandardDomainMenuItemOptions {
  domainTop: string;
  routes: readonly DomainRouteConfigEntry[];
  domainKey: string;
  domainLabel: string;
  icon: React.ReactNode;
  includeDashboard?: boolean;
  rootPathOverride?: string;
}

function buildStandardDomainMenuItem(options: StandardDomainMenuItemOptions): AppMenuItem {
  const [root] = toAppMenuItems(
    buildDomainNavigationItems({
      domainTop: options.domainTop,
      routes: options.routes,
      domainKey: options.domainKey,
      domainLabel: options.domainLabel,
      includeDashboard: options.includeDashboard ?? false,
    }),
    () => null
  );

  if (!root) {
    throw new Error(`buildStandardDomainMenuItem: ${options.domainTop} navigation SSOT is empty`);
  }

  const item = withDomainIcon(root, options.icon);
  if (options.rootPathOverride) {
    return { ...item, path: options.rootPathOverride };
  }
  return item;
}

export function buildFinanceDomainMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'finance',
    routes: financeSubRoutes,
    domainKey: 'finance',
    domainLabel: '재무',
    icon,
  });
}

export function buildFinanceAdvancedDomainMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'finance-advanced',
    routes: financeAdvancedSubRoutes,
    domainKey: 'finance-advanced',
    domainLabel: '고급 재무',
    icon,
  });
}

export function buildFinanceNavGroupChildren(icon: React.ReactNode): AppMenuItem[] {
  return [buildFinanceDomainMenuItem(icon), buildFinanceAdvancedDomainMenuItem(icon)];
}

export function buildEntityNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'entity',
    routes: entitySubRoutes,
    domainKey: 'entity',
    domainLabel: '구성원',
    icon,
    rootPathOverride: '/entity/internal',
  });
}

export function buildItemNavMenuItem(icon: React.ReactNode): AppMenuItem {
  const children = [
    ...leafItemsToAppMenu('item', itemSubRoutes),
    ...leafItemsToAppMenu('inventory', inventorySubRoutes),
  ];

  return {
    key: 'item',
    path: '/item/dashboard',
    icon,
    label: '아이템',
    children,
  };
}

export function buildCalendarNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'calendar',
    routes: calendarSubRoutes,
    domainKey: 'calendar',
    domainLabel: '일정',
    icon,
  });
}

const APPEAL_RELATION_FUNNEL_LEAF: AppMenuItem = {
  key: 'appeal-relation-funnel',
  path: '/entity/external',
  icon: null,
  label: '관계·퍼널',
};

export function buildAppealNavMenuItem(icon: React.ReactNode): AppMenuItem {
  const root = buildStandardDomainMenuItem({
    domainTop: 'appeal',
    routes: appealSubRoutes,
    domainKey: 'appeal',
    domainLabel: '대외 표현',
    icon,
  });

  const children = root.children ?? [];
  const campaignIndex = children.findIndex((child) => child.key === 'appeal-campaign');
  const insertAt = campaignIndex >= 0 ? campaignIndex + 1 : children.length;

  return {
    ...root,
    children: [
      ...children.slice(0, insertAt),
      APPEAL_RELATION_FUNNEL_LEAF,
      ...children.slice(insertAt),
    ],
  };
}

export function buildKnowledgeNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'knowledge',
    routes: knowledgeSubRoutes,
    domainKey: 'knowledge',
    domainLabel: '지식',
    icon,
  });
}

export function buildSpaceNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'space',
    routes: spaceSubRoutes,
    domainKey: 'space',
    domainLabel: '공간',
    icon,
  });
}

export function buildManufacturingNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'manufacturing',
    routes: manufacturingSubRoutes,
    domainKey: 'manufacturing',
    domainLabel: '제조',
    icon,
  });
}

export function buildLogNavMenuItem(icon: React.ReactNode): AppMenuItem {
  return buildStandardDomainMenuItem({
    domainTop: 'log',
    routes: logSubRoutes,
    domainKey: 'log',
    domainLabel: '로그',
    icon,
  });
}

export function buildOperationsNavGroupChildren(icon: {
  entity: React.ReactNode;
  item: React.ReactNode;
  calendar: React.ReactNode;
}): AppMenuItem[] {
  return [
    buildEntityNavMenuItem(icon.entity),
    buildItemNavMenuItem(icon.item),
    buildCalendarNavMenuItem(icon.calendar),
  ];
}

export function buildGrowthNavGroupChildren(icon: React.ReactNode): AppMenuItem[] {
  return [buildAppealNavMenuItem(icon)];
}

export function buildKnowledgeNavGroupChildren(icon: React.ReactNode): AppMenuItem[] {
  return [buildKnowledgeNavMenuItem(icon)];
}

export function buildSpaceNavGroupChildren(icon: React.ReactNode): AppMenuItem[] {
  return [buildSpaceNavMenuItem(icon)];
}

export function buildProductionNavGroupChildren(icon: {
  manufacturing: React.ReactNode;
  automation: React.ReactNode;
}): AppMenuItem[] {
  const automationRoute = automationSubRoutes[0];
  if (!automationRoute) {
    throw new Error('automationSubRoutes SSOT is empty');
  }
  return [
    buildManufacturingNavMenuItem(icon.manufacturing),
    {
      key: 'automation',
      path: buildAbsolutePath('automation', automationRoute.path),
      icon: icon.automation,
      label: resolveRouteLabel(automationRoute),
    },
  ];
}

export function buildDataNavGroupChildren(icon: {
  log: React.ReactNode;
  export: React.ReactNode;
}): AppMenuItem[] {
  const exportRoute = exportSubRoutes[0];
  if (!exportRoute) {
    throw new Error('exportSubRoutes SSOT is empty');
  }
  return [
    buildLogNavMenuItem(icon.log),
    {
      key: 'export',
      path: buildAbsolutePath('export', exportRoute.path),
      icon: icon.export,
      label: resolveRouteLabel(exportRoute),
    },
  ];
}

const EVENT_ROUTE_INDEX = new Map(eventSubRoutes.map((route) => [route.path, route] as const));
const WAREHOUSE_ROUTE_INDEX = new Map(
  warehouseSubRoutes.map((route) => [route.path, route] as const)
);
const FLOW_ROUTE_INDEX = new Map(flowSubRoutes.map((route) => [route.path, route] as const));

function buildConfigMenuLeaf(domainTop: string, route: DomainRouteConfigEntry): AppMenuItem {
  return {
    key: route.id ?? resolveRouteId(domainTop, route),
    path: buildAbsolutePath(domainTop, route.path),
    icon: null,
    label: resolveRouteLabel(route),
  };
}

function buildManualMenuLeaf(leaf: AppMenuItem): AppMenuItem {
  return { ...leaf, icon: null };
}

type FlowMenuLeafSpec =
  | { kind: 'manual'; leaf: AppMenuItem }
  | { kind: 'event'; routePath: string; adminOnly?: boolean }
  | { kind: 'warehouse'; routePath: string }
  | { kind: 'flow'; routePath: string };

interface FlowMenuGroupSpec {
  key: string;
  label: string;
  adminOnly?: boolean;
  leaves: FlowMenuLeafSpec[];
}

const FLOW_MENU_GROUP_SPECS: FlowMenuGroupSpec[] = [
  {
    key: 'flow-daily-group',
    label: '일상 업무',
    leaves: [
      { kind: 'flow', routePath: 'assignments' },
      { kind: 'flow', routePath: 'dispatch' },
      { kind: 'warehouse', routePath: 'picking-list' },
      { kind: 'warehouse', routePath: 'receiving' },
      { kind: 'warehouse', routePath: 'putaway' },
      { kind: 'warehouse', routePath: 'ops-board' },
      {
        kind: 'manual',
        leaf: {
          key: 'flow-approvals-pending',
          path: '/approvals/pending',
          icon: null,
          label: '승인 대기',
        },
      },
      {
        kind: 'manual',
        leaf: {
          key: 'flow-approvals-history',
          path: '/approvals/history',
          icon: null,
          label: '승인 이력',
        },
      },
    ],
  },
  {
    key: 'flow-mgmt-group',
    label: '흐름 관리',
    leaves: [
      { kind: 'flow', routePath: 'new' },
      { kind: 'event', routePath: 'flow-definition' },
      { kind: 'event', routePath: 'action-binding' },
      { kind: 'flow', routePath: 'streams' },
    ],
  },
  {
    key: 'flow-exec-group',
    label: '실행 기록',
    leaves: [
      { kind: 'event', routePath: 'flow-execution' },
      { kind: 'event', routePath: 'flow-event' },
    ],
  },
  {
    key: 'flow-legacy-group',
    label: '레거시 v1',
    adminOnly: true,
    leaves: [
      { kind: 'event', routePath: 'stream-definition', adminOnly: true },
      { kind: 'event', routePath: 'stream-execution', adminOnly: true },
      { kind: 'event', routePath: 'stream-event', adminOnly: true },
    ],
  },
];

/**
 * `menuVisible: false` on a `domainRouteConfig` entry means "keep the route
 * direct-reachable, but do not surface it in *ordinary* (non-admin) nav" (N1
 * menu-visibility enforcement). `buildConfigMenuLeaf` itself does not check
 * the flag, so any caller building a leaf directly from a route must gate on
 * it explicitly — this was previously missing here for `warehouse`/`flow`
 * leaves, leaking `menuVisible: false` routes into ordinary nav.
 *
 * `event`-kind leaves are the deliberate exception: `spec.adminOnly` (set on
 * the three deprecated-legacy stream leaves — see `flow-legacy-group` below)
 * intentionally re-surfaces a `menuVisible: false` route, but only inside an
 * `adminOnly: true` group that `AppSidebar`'s admin filtering hides from
 * ordinary users (`filterAdminOnlyItems`) — so `menuVisible: false` there is
 * still never visible in *ordinary* nav, just re-gated onto a different,
 * admin-only visibility mechanism instead of being hidden outright. Nulling
 * these out regardless of `spec.adminOnly` would silently empty and drop the
 * entire "레거시 v1" group (`buildFlowMenuGroups` filters empty groups),
 * removing legitimate admin-only visibility rather than fixing a leak.
 */
function resolveFlowMenuLeaf(spec: FlowMenuLeafSpec): AppMenuItem | null {
  if (spec.kind === 'manual') {
    return buildManualMenuLeaf(spec.leaf);
  }

  if (spec.kind === 'warehouse') {
    const route = WAREHOUSE_ROUTE_INDEX.get(spec.routePath);
    if (!route || route.menuVisible === false) {
      return null;
    }
    return buildConfigMenuLeaf('warehouse', route);
  }

  if (spec.kind === 'flow') {
    const route = FLOW_ROUTE_INDEX.get(spec.routePath);
    if (!route || route.menuVisible === false) {
      return null;
    }
    return buildConfigMenuLeaf('flow', route);
  }

  const route = EVENT_ROUTE_INDEX.get(spec.routePath);
  if (!route || (route.menuVisible === false && !spec.adminOnly)) {
    return null;
  }

  const leaf = buildConfigMenuLeaf('event', route);
  if (spec.adminOnly) {
    return { ...leaf, adminOnly: true };
  }
  return leaf;
}

function buildFlowMenuGroups(): AppMenuItem[] {
  return FLOW_MENU_GROUP_SPECS.map((group) => ({
    key: group.key,
    path: '',
    icon: null,
    label: group.label,
    type: 'group' as const,
    ...(group.adminOnly ? { adminOnly: true } : {}),
    children: group.leaves
      .map((leafSpec) => resolveFlowMenuLeaf(leafSpec))
      .filter((leaf): leaf is AppMenuItem => leaf !== null),
  })).filter((group) => group.children.length > 0);
}

export function buildFlowFooterMenuItem(icon: React.ReactNode): AppMenuItem {
  return {
    key: 'flow',
    path: '/flow',
    icon,
    label: '업무 흐름',
    children: buildFlowMenuGroups(),
  };
}

const WORKSPACE_FOOTER_MENU_ROUTE_IDS = new Set([
  'workspace-hub',
  'workspace-assignments',
  'workspace-tasks-index',
]);

export function buildWorkspaceFooterMenuItem(icon: React.ReactNode): AppMenuItem {
  const children = [
    ...workspaceSubRoutes
      .filter((route) => route.id != null && WORKSPACE_FOOTER_MENU_ROUTE_IDS.has(route.id))
      .map((route) => buildConfigMenuLeaf('workspace', route)),
    ...physicalAccessSubRoutes
      .filter((route) => route.menuVisible !== false)
      .map((route) => buildConfigMenuLeaf('physical-access', route)),
  ];

  return {
    key: 'workspace',
    path: buildAbsolutePath('workspace', ''),
    icon,
    label: '작업 허브',
    children,
  };
}

function buildSystemFooterMenuLeaf(route: DomainRouteConfigEntry): AppMenuItem {
  return {
    key: route.id ?? resolveRouteId('system', route),
    path: buildAbsolutePath('system', route.path),
    icon: null,
    label: resolveRouteLabel(route),
    ...(route.adminOnly ? { adminOnly: true } : {}),
  };
}

export function buildSystemFooterMenuItem(icon: React.ReactNode): AppMenuItem {
  const children = systemSubRoutes
    .filter((route) => route.menuVisible !== false)
    .map((route) => buildSystemFooterMenuLeaf(route));

  return {
    key: 'system',
    path: buildAbsolutePath('system', 'dashboard'),
    icon,
    label: '시스템',
    children,
  };
}

interface ConsciousMenuGroupSpec {
  key: string;
  label: string;
  hub?: { key: string; label: string; path: string };
  routePaths: string[];
  adminOnlyPaths?: string[];
}

const CONSCIOUS_MENU_GROUP_SPECS: ConsciousMenuGroupSpec[] = [
  {
    key: 'conscious-operator-group',
    label: '운영 모니터',
    hub: { key: 'conscious-hub', label: '모니터(허브)', path: '/conscious' },
    routePaths: ['alerts', 'suggestions'],
  },
  {
    key: 'conscious-advanced-group',
    label: '고급 분석',
    routePaths: ['ask', 'wisdom', 'stream', 'memory', 'learning', 'pattern', 'neuro'],
  },
  {
    key: 'conscious-system-group',
    label: '조직 상태',
    routePaths: ['org-state', 'brain'],
    adminOnlyPaths: ['brain'],
  },
];

const CONSCIOUS_ROUTE_INDEX = new Map(
  consciousSubRoutes.map((route) => [route.path, route] as const)
);

function buildConsciousMenuLeaf(route: DomainRouteConfigEntry, adminOnly = false): AppMenuItem {
  return {
    key: route.id ?? resolveRouteId('conscious', route),
    path: buildAbsolutePath('conscious', route.path),
    icon: null,
    label: resolveRouteLabel(route),
    ...(adminOnly ? { adminOnly: true } : {}),
  };
}

function buildConsciousMenuGroups(): AppMenuItem[] {
  return CONSCIOUS_MENU_GROUP_SPECS.map((group) => {
    const children: AppMenuItem[] = [];

    if (group.hub) {
      children.push({
        key: group.hub.key,
        path: group.hub.path,
        icon: null,
        label: group.hub.label,
      });
    }

    for (const routePath of group.routePaths) {
      const route = CONSCIOUS_ROUTE_INDEX.get(routePath);
      if (!route) {
        continue;
      }
      children.push(
        buildConsciousMenuLeaf(route, group.adminOnlyPaths?.includes(routePath) === true)
      );
    }

    return {
      key: group.key,
      path: '',
      icon: null,
      label: group.label,
      type: 'group',
      children,
    };
  });
}

export function buildConsciousFooterMenuItem(icon: React.ReactNode): AppMenuItem {
  return {
    key: 'conscious',
    path: '/conscious',
    icon,
    label: '조직 지능',
    children: buildConsciousMenuGroups(),
  };
}
